import { useEffect, useRef } from 'react'
import { type UseFormWatch, type UseFormSetValue } from 'react-hook-form'

import { type UserFormInput } from '@/lib/validation/user-schemas'

type FormData = UserFormInput
type RealmField = keyof FormData['realmRoles']
type AttrField = keyof FormData['attributes']

interface UseRoleSyncProps {
  watch: UseFormWatch<FormData>
  setValue: UseFormSetValue<FormData>
}

interface SyncFieldPair {
  realmField: RealmField
  attrField: AttrField
}

interface SyncContext {
  prev: Map<string, boolean>
  syncRef: React.MutableRefObject<boolean>
  setRealm: (val: boolean) => void
  setAttr: (val: boolean) => void
}

function applySyncAction(
  realmVal: boolean,
  attrVal: boolean,
  pair: SyncFieldPair,
  ctx: SyncContext
) {
  if (ctx.syncRef.current) return

  const realmChanged = realmVal !== ctx.prev.get(pair.realmField)
  const attrChanged = attrVal !== ctx.prev.get(pair.attrField)

  ctx.prev.set(pair.realmField, realmVal)
  ctx.prev.set(pair.attrField, attrVal)

  if (realmVal === attrVal) return

  ctx.syncRef.current = true

  // Both changed (mount with empty prev) — true wins (OR semantics).
  // If realm and attribute disagree on mount, the enabled side is kept
  // and the disabled side is synced to match, preserving any grant.
  if (realmChanged && attrChanged) {
    const target = realmVal ? ctx.setAttr : ctx.setRealm
    const key = realmVal ? pair.attrField : pair.realmField
    target(true)
    ctx.prev.set(key, true)
    ctx.syncRef.current = false
    return
  }

  if (realmChanged) {
    ctx.setAttr(realmVal)
    ctx.prev.set(pair.attrField, realmVal)
    ctx.syncRef.current = false
    return
  }

  if (attrChanged) {
    ctx.setRealm(attrVal)
    ctx.prev.set(pair.realmField, attrVal)
  }

  ctx.syncRef.current = false
}

const SYNC_PAIRS: SyncFieldPair[] = [
  { realmField: 'cs_systems_all', attrField: 'systems_all' },
  { realmField: 'cs_locations_all', attrField: 'locations_all' },
  { realmField: 'cs_sections_all', attrField: 'sections_all' },
]

interface PairEffectConfig {
  pair: SyncFieldPair
  realmRoles: FormData['realmRoles']
  attributes: FormData['attributes']
  setValue: UseFormSetValue<FormData>
  prev: React.MutableRefObject<Map<string, boolean>>
  syncRef: React.MutableRefObject<boolean>
}

function usePairEffect(config: PairEffectConfig) {
  const { pair, realmRoles, attributes, setValue, prev, syncRef } = config
  const realmVal = realmRoles[pair.realmField]
  const attrVal = attributes[pair.attrField]
  useEffect(() => {
    applySyncAction(realmVal === true, attrVal === true, pair, {
      prev: prev.current,
      syncRef,
      setRealm: (v) => {
        setValue(`realmRoles.${pair.realmField}`, v)
      },
      setAttr: (v) => {
        setValue(`attributes.${pair.attrField}`, v)
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- prev/syncRef are stable refs, pair is static
  }, [realmVal, attrVal, setValue])
}

export function useRoleSync({ watch, setValue }: UseRoleSyncProps) {
  const syncRef = useRef(false)
  const realmRoles = watch('realmRoles')
  const attributes = watch('attributes')
  const prevRef = useRef(new Map<string, boolean>())
  const shared = { realmRoles, attributes, setValue, prev: prevRef, syncRef }

  for (const pair of SYNC_PAIRS) {
    // Hook call count is constant — SYNC_PAIRS is a static array
    // eslint-disable-next-line react-hooks/rules-of-hooks
    usePairEffect({ pair, ...shared })
  }
}
